import { Inject, Injectable, LOCALE_ID } from '@angular/core';
import { interpret, StateMachine, InterpreterFrom, StateFrom } from 'xstate';
import { BehaviorSubject, fromEventPattern, Observable, of, Subject } from 'rxjs';
import Utils from '../../utils';
import { WorkflowsService } from '../../api/services/workflows.service';
import { WorkflowContext, WorkflowEvent } from './state.types';
import { createBookingModel, buildBookingMachine, BookingModel } from './booking.machine';
import { StateNamesEnum } from '../../enum/state-names-enum';
import { BreadcrumbService } from '../breadcrumb/breadcrumb.service';
import { Breadcrumb } from '../../components/breadcrumb/model/breadcrumb';
import { DynamicBreadcrumbItem } from '../../components/breadcrumb/interface/DynamicBreadcrumbItem';
import { WorkflowResponse } from '../../api/models/workflow-response';
import { AssignmentStrategyService } from '../assignment-strategy/assignment-strategy.service';
import { PersonalDataFormService } from '../../pages/personal-data/personal-data-api-bridge-service/personal-data-form.service';
import { BookingService } from '../booking/booking.service';
import { SessionStorageKeyEnum } from '../../enum/session-storage-key-enum';
import { Agency } from '../../api/models/agency';
import { Service } from '../../api/models/service';
import { SessionStorageService } from '../session-storage/session-storage.service';
import { LanguageISOType } from '../../types/LanguageISOType';
import { ApplicationSettingsService } from '../application-settings/application-settings.service';
import { StrategyRandom } from '../assignment-strategy/strategy/strategy-random';

type BookingMachine = ReturnType<typeof buildBookingMachine>;
type BookingInterpreter = InterpreterFrom<BookingMachine>;
type BookingState = StateFrom<BookingMachine>;

@Injectable({
    providedIn: 'root',
})
export class StateService {
    state$: Observable<any> = new Subject();
    private userModel: BookingModel;
    interpreter!: BookingInterpreter;
    private readonly updated$: Subject<boolean>;
    machineStarted$: Subject<boolean> = new Subject<boolean>();
    machine!: BookingMachine;
    private stateUpdatedSubject = new BehaviorSubject<boolean>(false);
    stateUpdated$ = this.stateUpdatedSubject.asObservable();

    constructor(
        @Inject(LOCALE_ID) public locale: string,
        private workflows: WorkflowsService,
        private breadcrumbService: BreadcrumbService,
        private assignmentStrategyService: AssignmentStrategyService,
        private personalDataService: PersonalDataFormService,
        private bookingService: BookingService,
        private sessionStorageService: SessionStorageService,
        private appSettingsService: ApplicationSettingsService,
    ) {
        this.updated$ = new Subject<boolean>();
        this.updated$.next(false);
    }

    init(): void {
        this.machine = this.initializeMachine();
        this.interpreter = interpret(this.machine, { devTools: true });

        this.state$ = fromEventPattern(
            (handler: any) => {
                this.interpreter.onTransition(handler).start(); // the machine startes here
                return this.interpreter;
            },
            (handler, service) => {
                service.stop();
            },
        );
        this.machineStarted$.next(true);
    }

    private initializeMachine(): StateMachine<WorkflowContext, any, WorkflowEvent> {
        /** get saved state from storage */
        const initialState: StateNamesEnum | undefined = this.getIntialMachineState();

        const machineConfigStorage = this.getStoredConfig();

        const machine: StateMachine<WorkflowContext, any, WorkflowEvent> = this.setupMachine(
            initialState,
            machineConfigStorage,
        );
        if (machineConfigStorage) {
            const agency = <Agency>this.bookingService.booking.agency;
            const service = <Service>this.bookingService.booking.service;
            if (agency && service) {
                this.updateMachine(service.id.toString(), agency.id.toString());
            }
        }

        return machine;
    }

    private getIntialMachineState(): StateNamesEnum | undefined {
        let initialState: StateNamesEnum | undefined;
        if (this.appSettingsService.serviceOnly) {
            initialState = StateNamesEnum.SERVICE_ONLY;
        }
        if (sessionStorage.getItem(SessionStorageKeyEnum.STATE_MACHINE_LAST_STATE)) {
            initialState = <StateNamesEnum>sessionStorage.getItem(SessionStorageKeyEnum.STATE_MACHINE_LAST_STATE);
        }
        if (!initialState) {
            initialState = undefined;
        }

        return initialState;
    }

    setupMachine(
        initialState: StateNamesEnum = StateNamesEnum.COURT_AND_SERVICE,
        machineConfigStorage?: string,
    ): BookingMachine {
        this.userModel = createBookingModel({
            hasAssignment: true,
            hasParticipantsCount: true,
            mustAcceptTerms: true,
            mustAcceptEmailConsent: false,
            bookingTimeframe: 3,
            serviceOnly: false,
            hasAppointmentType: false,
        } as WorkflowContext);

        return buildBookingMachine(this.userModel, {
            initialState,
            storedConfig: machineConfigStorage,
            serviceOnlyFlag: this.appSettingsService.serviceOnly,
        });
    }

    private getStoredConfig(): string | undefined {
        const machineConfigStorage = sessionStorage.getItem(SessionStorageKeyEnum.STATE_MACHINE_CONFIG);
        if (!machineConfigStorage) return undefined;

        try {
            const parsed = JSON.parse(machineConfigStorage);

            const isValidStoredConfig = parsed && typeof parsed === 'object' && parsed.id === 'booking';

            if (!isValidStoredConfig) {
                throw new Error('Invalid machine snapshot shape');
            }
            return machineConfigStorage;
        } catch (e) {
            this.sessionStorageService.clearWorkflowAndBooking();
            return undefined;
        }
    }

    updateMachineNoAgency(serviceId: string = '2'): Observable<boolean> {
        this.handleNoAgencyState(serviceId);
        return of(true);
    }

    updateMachine(serviceId: string = '2', agencyId: string): Observable<boolean> {
        this.updated$.next(false);

        const workflowResponse$ = this.workflows.getApiPublicWorkflows({
            agency_id: agencyId,
            service_id: serviceId,
            _locale: this.locale.toLowerCase() as LanguageISOType,
        });

        workflowResponse$.subscribe((workflowResponse: WorkflowResponse) => {
            const data = workflowResponse.data;
            const settings = data.settings;
            let service_only = false;
            let hasAppointmentType = false;
            // Todo: refactor to own service
            if (
                (this.bookingService.booking.service && !this.bookingService.booking.agency) ||
                this.appSettingsService.serviceOnly
            ) {
                settings.has_assignment = false;
                settings.has_participants_count = false;
                settings.booking_timeframe = 3;
                service_only = true;
            }
            if (this.appSettingsService.onlineService && this.bookingService.booking.service?.meeting_type == 'both') {
                hasAppointmentType = true;
            }
            this.interpreter.send(
                this.userModel.events.UPDATE(
                    settings.has_assignment,
                    settings.has_participants_count,
                    settings.must_accept_terms,
                    settings.must_accept_email_consent,
                    settings.booking_timeframe,
                    service_only,
                    hasAppointmentType,
                ),
            );
            this.stateUpdatedSubject.next(true);
            this.updated$.next(true);

            // Configure assignment strategy
            if (settings.has_assignment) {
                const newStrategy = this.assignmentStrategyService.createStrategyFromAssignment(data.assignment);
                this.bookingService.booking.strategy = newStrategy;
            } else {
                this.bookingService.booking.strategy = new StrategyRandom();
            }
            this.bookingService.booking.hasAssignmentStrategy = settings.has_assignment;
            this.bookingService.booking.nextRelease = workflowResponse.data.next_release;
            this.bookingService.booking.hasParticipantsCount = settings.has_participants_count;
            if (settings.has_participants_count && workflowResponse.data.automatic_extension) {
                this.bookingService.booking.automaticExtension = workflowResponse.data.automatic_extension;
                this.bookingService.booking.aESignal.set(this.bookingService.booking.automaticExtension);
            }
            // we add form data here
            this.personalDataService.personalDataForm.next(data.forms);
        });

        return this.updated$;
    }

    handleNoAgencyState(_serivceId: string): void {
        this.machine = this.setupMachine(StateNamesEnum.SERVICE_ONLY);
    }

    getCurrentStepNumber(): number {
        const myEndRange: number = this.getCurrentStepMeta();
        return this.calculateStep(myEndRange);
    }

    private getCurrentStepMeta(): number {
        return this.interpreter.state.meta[this.interpreter.machine.id + '.' + this.interpreter.state.value].step;
    }

    getCurrentState(): BookingState {
        return this.interpreter.state;
    }

    getStepCount(): number {
        return this.calculateStep(this.interpreter.machine.states[StateNamesEnum.THANK_YOU].meta.step);
    }

    next(): BookingState {
        return this.interpreter.send({ type: 'NEXT' });
    }

    back(): BookingState {
        return this.interpreter.send({ type: 'BACK' });
    }

    switchToTimeFirst(): BookingState {
        return this.interpreter.send({ type: 'SWITCH_TO_TIME_FIRST' });
    }

    switchToDateFirst(): BookingState {
        return this.interpreter.send({ type: 'SWITCH_TO_DATE_FIRST' });
    }

    backToServiceOnly(): BookingState {
        return this.interpreter.send({ type: 'BACK_TO_SERVICE_ONLY' });
    }

    backToCourtAndService(): BookingState {
        return this.interpreter.send({ type: 'BACK_TO_COURT_AND_SERVICE' });
    }

    backToAssignmentStrategy(): BookingState {
        return this.interpreter.send({ type: 'BACK_TO_ASSIGNMENT_STRATEGY' });
    }

    backToAppointmentType(): BookingState {
        return this.interpreter.send({ type: 'BACK_TO_APPOINTMENT_TYPE' });
    }

    backToParticipantsCount(): BookingState {
        return this.interpreter.send({ type: 'BACK_TO_PARTICIPANT_COUNT' });
    }

    backToDateFirst(): BookingState {
        return this.interpreter.send({ type: 'BACK_TO_DATE_FIRST' });
    }

    backToPersonalData(): BookingState {
        return this.interpreter.send({ type: 'BACK_TO_PERSONAL_DATA' });
    }

    backToSummary(): BookingState {
        return this.interpreter.send({ type: 'BACK_TO_SUMMARY' });
    }

    private calculateStep(endRange: number): number {
        let range = Utils.range(endRange, 1);
        if (!this.interpreter.state.context.hasAssignment) {
            range = range.filter((el) => 2 !== el);
        }
        if (!this.interpreter.state.context.hasParticipantsCount) {
            range = range.filter((el) => 3 !== el);
        }
        return range.length;
    }

    getActiveSteps(): StateNamesEnum[] {
        const currentStepMeta = this.getCurrentStepMeta();
        const breadcrumbs: Breadcrumb[] = [];
        const activeSteps: StateNamesEnum[] = [];
        const currentState = this.interpreter.getSnapshot();
        const serviceOnlyActive = this.appSettingsService.serviceOnly;
        for (const state in this.interpreter.machine.states) {
            const stateNode = this.interpreter.machine.states[state];
            const metaData: DynamicBreadcrumbItem = stateNode.meta;
            const stepOfItem: number = metaData.step;

            if (stepOfItem <= currentStepMeta) {
                if (!metaData.breadcrumbName) {
                    continue;
                }

                // Skip breacrumb for agency & service selection when service only is on
                if (serviceOnlyActive) {
                    if (stateNode.key === StateNamesEnum.COURT_AND_SERVICE) {
                        continue;
                    }
                } else {
                    if (stateNode.key === StateNamesEnum.SERVICE_ONLY) {
                        continue;
                    }
                }
                if (stateNode.key === StateNamesEnum.APPOINTMENT_TYPE && !currentState.context.hasAppointmentType) {
                    continue;
                }
                if (
                    (stateNode.key === StateNamesEnum.ASSIGNMENT_STRATEGY && !currentState.context.hasAssignment) || // skip when assignment strategy is not active
                    (stateNode.key === StateNamesEnum.ASSIGNMENT_STRATEGY && serviceOnlyActive) // skip assignment strategy when service only is on
                ) {
                    continue;
                }
                if (
                    (stateNode.key === StateNamesEnum.PARTICIPANT_COUNT &&
                        !currentState.context.hasParticipantsCount) || // skip when participants_count is not active
                    (stateNode.key === StateNamesEnum.PARTICIPANT_COUNT && serviceOnlyActive) // skip participants when service only is on
                ) {
                    continue;
                }

                if (
                    (stateNode.id === 'booking.time_first' && currentState.value === StateNamesEnum.DATE_FIRST) ||
                    (stateNode.id === 'booking.date_first' && currentState.value === StateNamesEnum.TIME_FIRST)
                ) {
                    continue;
                }
                const bc = this.breadcrumbService.createByCurrentStateMachine(
                    metaData,
                    <StateNamesEnum>state,
                    currentStepMeta,
                );
                breadcrumbs.push(bc);
                activeSteps.push(state as StateNamesEnum);
            }
        }

        this.breadcrumbService.activeBreadCrumbs.next(this.filterSameBreadcrumbEntry(breadcrumbs));

        return this.filterSameStepEntry(activeSteps);
    }

    private filterSameBreadcrumbEntry(steps: Breadcrumb[]): Breadcrumb[] {
        const currentStates = steps.map((bc) => bc.url);
        if (currentStates.includes(StateNamesEnum.DATE_FIRST) && currentStates.includes(StateNamesEnum.TIME_FIRST)) {
            steps = steps.filter((step) => step.url !== StateNamesEnum.TIME_FIRST);
        }
        return steps;
    }

    private filterSameStepEntry(steps: StateNamesEnum[]): StateNamesEnum[] {
        if (steps.includes(StateNamesEnum.DATE_FIRST) && steps.includes(StateNamesEnum.TIME_FIRST)) {
            steps = steps.filter((step) => step !== StateNamesEnum.TIME_FIRST);
        }
        return steps;
    }

    detectDirection(newURL: string, oldURL: string): string {
        const states = this.interpreter.machine.states;
        const stateNewSplit = newURL.split('/');
        const stateNew = stateNewSplit[stateNewSplit.length - 1];
        const stateOldSplit = oldURL.split('/');
        const stateOld = stateOldSplit[stateOldSplit.length - 1];
        if (!states[stateNew] || !states[stateOld]) {
            return '';
        }
        const orderNumber = states[stateNew]?.order - states[stateOld]?.order;
        if (orderNumber > 0) {
            return 'forward';
        } else if (orderNumber < 0) {
            return 'backward';
        }
        return 'nochange';
    }
}
